import React, { useState, useEffect, useMemo } from "react";
import { adminAPI } from "../services/auth";
import { useToasts } from "../hooks/useToasts";
import Spinner from "../components/Spinner";
import Button from "../components/Button";
import Filter from "../components/Filter";
import VerificationButton from "../components/VerificationButton";
import "./Admin.css";

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [sortColumn, setSortColumn] = useState("email");
  const [sortDirection, setSortDirection] = useState("asc");
  const { addToast } = useToasts();

  const sortedUsers = useMemo(() => {
    const filtered = users.filter((user) => {
      if (user.role === "superadmin") return false;

      const isBlocked =
        user.lockedUntil && new Date(user.lockedUntil) > new Date();
      const roleMatch = roleFilter === "all" || user.role === roleFilter;
      const statusMatch =
        statusFilter === "all" ||
        (statusFilter === "active" && !isBlocked && user.isActive) ||
        (statusFilter === "blocked" && isBlocked) ||
        (statusFilter === "deactivated" && !isBlocked && !user.isActive);
      const verificationMatch =
        verificationFilter === "all" ||
        (verificationFilter === "verified" && user.isEmailVerified) ||
        (verificationFilter === "unverified" && !user.isEmailVerified);
      return roleMatch && statusMatch && verificationMatch;
    });

    return [...filtered].sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case "email":
          aVal = (a.email || "").toLowerCase();
          bVal = (b.email || "").toLowerCase();
          break;
        case "index":
          aVal = a.index || 0;
          bVal = b.index || 0;
          break;
        case "status": {
          const getStatus = (user) => {
            const isBlocked =
              user.lockedUntil && new Date(user.lockedUntil) > new Date();
            if (isBlocked) return "blocked";
            if (!user.isActive)
              return user.deactivatedBy === "self"
                ? "self deactivated"
                : "deactivated";
            return "active";
          };
          aVal = getStatus(a);
          bVal = getStatus(b);
          break;
        }
        case "createdAt":
          aVal = new Date(a.createdAt || 0);
          bVal = new Date(b.createdAt || 0);
          break;
        case "isEmailVerified":
          aVal = a.isEmailVerified ? 1 : 0;
          bVal = b.isEmailVerified ? 1 : 0;
          break;
        case "lastLoginAt":
          aVal = new Date(a.lastLoginAt || 0);
          bVal = new Date(b.lastLoginAt || 0);
          break;
        case "role":
          aVal = a.role || "";
          bVal = b.role || "";
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [
    users,
    sortColumn,
    sortDirection,
    roleFilter,
    statusFilter,
    verificationFilter,
  ]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const res = await adminAPI.getAllUsers();
        setUsers(res.data.users);
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] || "Failed to load users",
          "error-warning"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [addToast]);

  const handleRoleChange = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    try {
      await adminAPI.changeUserRole(userId, newRole);
      setUsers(
        users.map((u) => (u._id === userId ? { ...u, role: newRole } : u))
      );
      addToast(`Role changed to ${newRole}`, "success-info");
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.error === "Cannot change last admin role") {
        addToast("Cannot change role of last admin", "error-warning");
      } else if (errorData?.error === "Cannot promote user to admin") {
        const fullMessage = errorData?.details?.[0] || "";
        const parts = fullMessage.split(". ");
        if (parts.length >= 2) {
          addToast(parts[0] + ". " + parts[1], "error-warning");
          addToast(parts[2], "error-warning");
        } else {
          addToast(fullMessage, "error-warning");
        }
      } else {
        addToast(errorData?.details?.[0] || "Failed to change role", "error-warning");
      }
    }
  };

  const handleBlockToggle = async (userId, isBlocked) => {
    try {
      await adminAPI.blockUser(userId, !isBlocked);
      const newLockedUntil = !isBlocked
        ? new Date(Date.now() + 100 * 365 * 24 * 60 * 60 * 1000)
        : null;
      setUsers(
        users.map((u) =>
          u._id === userId ? { ...u, lockedUntil: newLockedUntil } : u
        )
      );
      if (isBlocked) {
        addToast("User unblocked", "success-info");
      } else {
        addToast("User blocked", "success-info");
        addToast("User cannot login", "error-warning");
        addToast("Content remains on site", "success-info");
      }
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update user",
        "error-warning"
      );
    }
  };

  const handleStatusToggle = async (userId, isActive) => {
    try {
      await adminAPI.toggleUserStatus(userId);
      setUsers(
        users.map((u) => (u._id === userId ? { ...u, isActive: !isActive } : u))
      );
      if (isActive) {
        const userRole = users.find((u) => u._id === userId)?.role || "User";
        addToast(
          `${userRole.charAt(0).toUpperCase() + userRole.slice(1)} deactivated`,
          "success-info"
        );
        addToast("User cannot login", "error-warning");
        addToast("Content hidden from site", "success-info");
      } else {
        const userRole = users.find((u) => u._id === userId)?.role || "User";
        addToast(
          `${userRole.charAt(0).toUpperCase() + userRole.slice(1)} reactivated`,
          "success-info"
        );
      }
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update user status",
        "error-warning"
      );
    }
  };

  const handleSendVerification = async (userId) => {
    try {
      await adminAPI.sendVerificationEmail(userId);
      addToast("Verification email sent", "success-info");
      addToast("Next email available in 3 minutes", "success-info");
    } catch (error) {
      if (error.response?.status === 429) {
        const timeLeft = error.response?.data?.timeLeft;
        const minutes = Math.ceil(timeLeft / 60000);
        addToast(
          `Please wait ${minutes} minutes before sending another verification email`,
          "error-warning"
        );
      } else {
        addToast(
          error.response?.data?.details?.[0] ||
            "Failed to send verification email",
          "error-warning"
        );
      }
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  if (loading) return <Spinner />;

  const filteredUsers = sortedUsers;

  const handleResetFilters = () => {
    setRoleFilter("all");
    setStatusFilter("all");
    setVerificationFilter("all");
  };

  const hasActiveFilters =
    roleFilter !== "all" ||
    statusFilter !== "all" ||
    verificationFilter !== "all";

  return (
    <div className="form-content-frame table-page">
      <div className="admin-header">
        <h1>Users</h1>
        <Button
          className={`button-reset${
            hasActiveFilters ? " button-reset-visible" : ""
          }`}
          onClick={handleResetFilters}
        >
          Reset Filters
        </Button>
      </div>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th className="admin-header-cell">
                <div className="admin-header-title">No</div>
              </th>
              <th
                onClick={() => handleSort("email")}
                className="admin-header-cell admin-header-cell-sortable"
              >
                <div className="admin-header-title">Email</div>
                <div className="admin-header-arrows">
                  {sortColumn === "email"
                    ? sortDirection === "asc"
                      ? "↑↓"
                      : "↓↑"
                    : "↑↓"}
                </div>
              </th>
              <th
                onClick={() => handleSort("index")}
                className="admin-header-cell admin-header-cell-sortable"
              >
                <div className="admin-header-title">Index User ID</div>
                <div className="admin-header-arrows">
                  {sortColumn === "index"
                    ? sortDirection === "asc"
                      ? "↑↓"
                      : "↓↑"
                    : "↑↓"}
                </div>
              </th>
              <th className="admin-header-cell">
                <div className="admin-header-title">Status</div>
                <Filter
                  className="admin-header-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="blocked">Blocked</option>
                  <option value="deactivated">Deactivated</option>
                </Filter>
              </th>
              <th
                onClick={() => handleSort("createdAt")}
                className="admin-header-cell admin-header-cell-sortable"
              >
                <div className="admin-header-title">Created</div>
                <div className="admin-header-arrows">
                  {sortColumn === "createdAt"
                    ? sortDirection === "asc"
                      ? "↑↓"
                      : "↓↑"
                    : "↑↓"}
                </div>
              </th>
              <th className="admin-header-cell">
                <div className="admin-header-title">Email Verification</div>
                <Filter
                  className="admin-header-filter"
                  value={verificationFilter}
                  onChange={(e) => setVerificationFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="verified">Verified</option>
                  <option value="unverified">Unverified</option>
                </Filter>
              </th>
              <th
                onClick={() => handleSort("lastLoginAt")}
                className="admin-header-cell admin-header-cell-sortable"
              >
                <div className="admin-header-title">Last Login</div>
                <div className="admin-header-arrows">
                  {sortColumn === "lastLoginAt"
                    ? sortDirection === "asc"
                      ? "↑↓"
                      : "↓↑"
                    : "↑↓"}
                </div>
              </th>
              <th className="admin-header-cell">
                <div className="admin-header-title">Role</div>
                <Filter
                  className="admin-header-filter"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </Filter>
              </th>
              <th className="admin-header-cell">
                <div className="admin-header-title">Change Role</div>
              </th>
              <th className="admin-header-cell">
                <div className="admin-header-title">Block Unblock</div>
              </th>
              <th className="admin-header-cell">
                <div className="admin-header-title">Deactivate Reactivate</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="11" className="admin-empty-message">
                  No users found
                </td>
              </tr>
            ) : (
              filteredUsers.map((user, index) => {
                const isBlocked =
                  user.lockedUntil && new Date(user.lockedUntil) > new Date();
                return (
                  <tr key={user._id}>
                    <td data-label="No">{index + 1}</td>
                    <td data-label="Email" className="admin-email-cell">{user.email}</td>
                    <td data-label="Index / ID" className="admin-id-cell">
                      <div>{user.index || "-"}</div>
                      <div className="admin-id-secondary">{user._id}</div>
                    </td>
                    <td data-label="Status">
                      <span
                        className={`admin-status-badge admin-status-${
                          isBlocked
                            ? "blocked"
                            : !user.isActive
                            ? "blocked"
                            : "active"
                        }`}
                      >
                        {isBlocked
                          ? "Blocked"
                          : !user.isActive
                          ? user.deactivatedBy === "self"
                            ? "Self Deactivated"
                            : "Admin Deactivated"
                          : "Active"}
                      </span>
                    </td>
                    <td data-label="Created">{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td data-label="Verification">
                      {user.role === "admin" ? (
                        <span className="admin-status-badge admin-status-protected">
                          Protected
                        </span>
                      ) : user.isEmailVerified ? (
                        <span className="admin-verification-badge admin-verification-verified">
                          Verified
                        </span>
                      ) : (
                        <VerificationButton
                          userId={user._id}
                          onSend={() => handleSendVerification(user._id)}
                        />
                      )}
                    </td>
                    <td data-label="Last Login">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString()
                        : "Never"}
                    </td>
                    <td data-label="Role">
                      <span
                        className={`admin-role-badge admin-role-${user.role}`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td data-label="Change Role">
                      {user.role === "admin" ? (
                        <span className="admin-status-badge admin-status-protected">
                          Protected
                        </span>
                      ) : (
                        <Button
                          className={
                            user.role === "admin"
                              ? "button-btn-light-green"
                              : "button-btn-light-blue"
                          }
                          onClick={() => {
                            if (user.role !== "admin" && isBlocked) {
                              addToast("Unblock user to make admin", "error-warning");
                            } else {
                              handleRoleChange(user._id, user.role);
                            }
                          }}
                        >
                          {user.role === "admin" ? "Make User" : "Make Admin"}
                        </Button>
                      )}
                    </td>
                    <td data-label="Block / Unblock">
                      {user.role === "admin" ? (
                        <span className="admin-status-badge admin-status-protected">
                          Protected
                        </span>
                      ) : !user.isActive ? (
                        <span className="admin-status-badge admin-status-protected">
                          Disabled
                        </span>
                      ) : (
                        <Button
                          className={
                            isBlocked
                              ? "button-btn-light-green"
                              : "button-btn-light-red"
                          }
                          onClick={() => handleBlockToggle(user._id, isBlocked)}
                        >
                          {isBlocked ? "Unblock" : "Block"}
                        </Button>
                      )}
                    </td>
                    <td data-label="Deactivate / Reactivate">
                      {user.role === "admin" ? (
                        <span className="admin-status-badge admin-status-protected">
                          Protected
                        </span>
                      ) : (
                        <Button
                          className={
                            user.isActive
                              ? "button-btn-light-red"
                              : "button-btn-light-green"
                          }
                          onClick={() => {
                            if (user.isActive && isBlocked) {
                              addToast(
                                "Unblock user before deactivating",
                                "error-warning"
                              );
                            } else {
                              handleStatusToggle(user._id, user.isActive);
                            }
                          }}
                        >
                          {user.isActive ? "Deactivate" : "Reactivate"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Admin;
